import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  eWayBillAuthTokenAction,
  eWayBillInvoiceDataAction,
  eWayBillCreateAction,
} from "../../../redux/actions/sales-action";
import { storeDetailsAction } from "../../../redux/actions/invoices-action";
import errorToast from "../../helper-components/error-toast";
import successToast from "../../helper-components/success-toast";
import moment from "moment";
import { Truck, Train, Plane, Ship, ChevronDown, ChevronUp, MapPin } from "lucide-react";
import LoadingSpinner from "../../helper-components/loading-spinner";
import InputField from "../../forms/input-field";
import SelectField from "../../forms/select-field";
import AddressSidebarSales from "../../common-components/address-bar-sales";

/* ─── icon map for transport modes (keyed by name from API) ─────────────────── */
const MODE_ICONS = { Road: Truck, Rail: Train, Air: Plane, Ship: Ship };

/* ─── helper: convert API name/value|code array → react-select options ─────────── */
const toOptions = (arr = []) =>
  arr.map((item) => ({
    label: item?.name ?? item?.label ?? "",
    value: item?.value ?? item?.code ?? "",   // states use "code" not "value"
  }));

/* ─── shared layout constants ───────────────────────────────────────────────── */
const LW = "10rem";
const LABEL_CLASS =
  "whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]";
const PARENT_CLASS = "flex items-center m-0 p-0 w-full";

/* ─── header bar style: compact select on blue bg ───────────────────────────── */
const headerSelectStyle = {
  control: (base) => ({
    ...base,
    border: "none",
    boxShadow: "none",
    minHeight: "38px",
    fontFamily: "Inter, sans-serif",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
  }),
};

/* ─── address display column ─────────────────────────────────────────────────── */
const AddressCol = ({ title, lines = [], gstin }) => (
  <div className="flex-1 min-w-0">
    <p
      className="font-[600] text-[var(--nav-heading-color)] uppercase mb-2"
      style={{ fontSize: "11px", letterSpacing: "0.05em" }}
    >
      {title}
    </p>
    {gstin && (
      <p className="mb-1">
        <span
          className="inline-block rounded px-[6px] py-[2px] font-[600] bg-[#eaf1fb] text-[var(--primary)] border border-[#c3d8f5]"
          style={{ fontSize: "11px", letterSpacing: "0.02em" }}
        >
          GSTIN: {gstin}
        </span>
      </p>
    )}
    {lines.filter(Boolean).length > 0 ? (
      lines.filter(Boolean).map((line, i) => (
        <p key={i} className="text-[var(--secondary)]" style={{ fontSize: "12px", lineHeight: "1.7" }}>
          {line}
        </p>
      ))
    ) : (
      <p className="text-[var(--placeholder-color)]" style={{ fontSize: "12px" }}>—</p>
    )}
  </div>
);

/* ─── inner form ─────────────────────────────────────────────────────────────── */
const EWayBillFormInner = ({
  orderData,
  itemData,
  storeDetails,
  storeData,
  dropdownData,
  invNo,
  taxableTotal,
  igstTotal,
  cgstTotal,
  sgstTotal,
  grandTotal,
}) => {
  const navigate = useNavigate();
  const { values, setFieldValue, submitForm, isSubmitting } = useFormikContext();

  const [itemDetailsOpen, setItemDetailsOpen] = useState(true);
  const [addressPanel, setAddressPanel] = useState(false);
  const addressRef = useRef(null);

  /* ── address lines ── */
  const payAddr = values.ewb_pay_to_address;
  const shipAddr = values.ewb_ship_to_address;

  /* prefer storeData from invoice API (company_* fields); fall back to storeDetails */
  const storeLine1 = storeData?.company_address ?? storeDetails?.store_address ?? storeDetails?.address_line1 ?? "";
  const storeCity = storeData?.company_city ?? storeDetails?.city ?? storeDetails?.store_city ?? "";
  const storeState = storeData?.company_state ?? storeDetails?.state ?? storeDetails?.store_state ?? "";
  const storePincode = storeData?.company_pincode ?? storeDetails?.pincode ?? storeDetails?.store_pincode ?? "";
  const storeCountry = storeDetails?.country ?? storeDetails?.store_country ?? "";

  const storeLines = [
    storeLine1,
    [storeCity, storeState].filter(Boolean).join(", "),
    storePincode ? `Pincode: ${storePincode}` : "",
  ];

  const billToLines = payAddr
    ? [
      payAddr.cmr_pay_address_street,
      payAddr.cmr_pay_address_address,
      [payAddr.cmr_pay_address_city, payAddr.cmr_pay_address_district].filter(Boolean).join(", "),
      payAddr.cmr_pay_address_state,
      payAddr.cmr_pay_address_country,
      (payAddr.cmr_pay_pincode ?? payAddr.cmr_pay_address_pincode)
        ? `Pincode: ${payAddr.cmr_pay_pincode ?? payAddr.cmr_pay_address_pincode}`
        : null,
    ]
    : [
      orderData?.cmr_billing_address1,
      orderData?.cmr_billing_address2,
      [orderData?.cmr_billing_city, orderData?.cmr_billing_district].filter(Boolean).join(", "),
      orderData?.cmr_billing_state,
      orderData?.cmr_billing_country,
      orderData?.cmr_billing_pincode ? `Pincode: ${orderData.cmr_billing_pincode}` : null,
    ];

  const shipToLines = shipAddr
    ? [
      shipAddr.cmr_ship_address_street,
      shipAddr.cmr_ship_address_address,
      [shipAddr.cmr_ship_address_city, shipAddr.cmr_ship_address_district].filter(Boolean).join(", "),
      shipAddr.cmr_ship_address_state,
      shipAddr.cmr_ship_address_country,
      (shipAddr.cmr_ship_pincode ?? shipAddr.cmr_ship_address_pincode)
        ? `Pincode: ${shipAddr.cmr_ship_pincode ?? shipAddr.cmr_ship_address_pincode}`
        : null,
    ]
    : [
      orderData?.cmr_shipping_address1 || orderData?.cmr_billing_address1,
      orderData?.cmr_shipping_address2 || orderData?.cmr_billing_address2,
      [orderData?.cmr_shipping_city || orderData?.cmr_billing_city,
      orderData?.cmr_shipping_district || orderData?.cmr_billing_district].filter(Boolean).join(", "),
      orderData?.cmr_shipping_state || orderData?.cmr_billing_state,
      orderData?.cmr_shipping_country || orderData?.cmr_billing_country,
      (orderData?.cmr_shipping_pincode || orderData?.cmr_billing_pincode)
        ? `Pincode: ${orderData?.cmr_shipping_pincode || orderData?.cmr_billing_pincode}`
        : null,
    ];

  /* ── resolved dropdown option arrays ── */
  const docTypeOptions = toOptions(dropdownData.docType);
  const subSupplyOptions = toOptions(dropdownData.subSupplyType);
  const transTypeOptions = toOptions(dropdownData.transactionType);
  const stateOptions = toOptions(dropdownData.states);
  const transModeList = dropdownData.transMode ?? [];
  const vehicleTypeList = dropdownData.vehicleType ?? [];

  return (
    <Form className="h-full flex flex-col">

      {/* ════ SCROLLABLE BODY ══════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto">

        {/* ── Header bar ────────────────────────────────────────────────────── */}
        <div
          className="bg-[var(--add-billing-head-bg)] px-6 flex items-center gap-5 flex-wrap"
          style={{ minHeight: "5.5rem", borderBottom: "1px solid var(--light-border-color)" }}
        >
          <AddressSidebarSales
            open={addressPanel}
            setOpen={setAddressPanel}
            id={values.customer_id}
            name="ewb"
            page="add"
            pageType="invoice"
            autoDraft={() => { }}
            disabled={false}
          />

          {/* Document Type */}
          <div className="w-[200px]">
            <SelectField
              name="document_type"
              label="Document Type"
              placeholder="Select type"
              options={docTypeOptions}
              isRequired={true}
              labelWidth="0px"
              containerClass="flex flex-col gap-[3px]"
              parentClass="flex flex-col m-0 p-0 w-full"
              labelClass="whitespace-nowrap text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              defaultInputClass="min-h-[38px] rounded-[8px] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)]"
              inputBorder="border border-[var(--search-input-border)]"
              errMsgContainerClass="hidden"
              style={headerSelectStyle}
            />
          </div>

          {/* Transaction Sub Type */}
          <div className="w-[200px]">
            <SelectField
              name="transaction_sub_type"
              label="Transaction Sub Type"
              placeholder="Select sub type"
              options={subSupplyOptions}
              isRequired={true}
              labelWidth="0px"
              containerClass="flex flex-col gap-[3px]"
              parentClass="flex flex-col m-0 p-0 w-full"
              labelClass="whitespace-nowrap text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              defaultInputClass="min-h-[38px] rounded-[8px] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)]"
              inputBorder="border border-[var(--search-input-border)]"
              errMsgContainerClass="hidden"
              style={headerSelectStyle}
            />
          </div>

          {/* Customer Name */}
          <div className="w-[200px]">
            <SelectField
              name="customer_name_display"
              label="Customer Name"
              placeholder="Select customer"
              options={orderData?.cmr_name ? [{ label: orderData.cmr_name, value: orderData.cmr_name }] : []}
              isRequired={true}
              labelWidth="0px"
              containerClass="flex flex-col gap-[3px]"
              parentClass="flex flex-col m-0 p-0 w-full"
              labelClass="whitespace-nowrap text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              defaultInputClass="min-h-[38px] rounded-[8px] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)]"
              inputBorder="border border-[var(--search-input-border)]"
              errMsgContainerClass="hidden"
              style={headerSelectStyle}
            />
          </div>

          {/* Invoice # — editable input (pre-filled from API, user can shorten if > 16 chars) */}
          <div className="w-[200px]">
            <InputField
              name="doc_no"
              label="Invoice #"
              placeholder="Invoice number"
              isRequired={true}
              labelWidth="0px"
              containerClass="flex flex-col gap-[3px]"
              parentClass="flex flex-col m-0 p-0 w-full"
              labelClass="whitespace-nowrap text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              defaultInputClass="min-h-[38px] rounded-[8px] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border border-[var(--search-input-border)] px-3"
              errMsgContainerClass="hidden"
              allowSpecial={true}
            />
          </div>

          {/* Date — from created_date */}
          {orderData?.created_date && (
            <div className="flex flex-col gap-[3px]">
              <span
                className="text-[var(--search-input-label-color)] font-[var(--search-label-font-weight)]"
                style={{ fontSize: "var(--search-label-font-size, 12px)" }}
              >
                Date
              </span>
              <div
                className="border border-[var(--search-input-border)] rounded-[8px] px-3 bg-[var(--search-input-bg)] text-[var(--search-input-color)] font-[var(--search-input-font-weight)] flex items-center"
                style={{ minHeight: "38px", fontSize: "var(--search-input-font-size, 13px)", minWidth: "8rem" }}
              >
                {moment(orderData.created_date).isValid()
                  ? moment(orderData.created_date).format("DD MMM YYYY")
                  : orderData.created_date}
              </div>
            </div>
          )}

          <div className="flex-1" />
        </div>

        {/* ════ FORM BODY ══════════════════════════════════════════════════════ */}
        <div className="px-6 py-5 flex flex-col gap-0">

          {/* ── Section 1: Transaction Type + Address Details + Place of Delivery */}
          <section className="pb-5 border-b border-[var(--light-border-color)] flex flex-col gap-4">

            {/* Transaction Type */}
            <SelectField
              name="transaction_type"
              label="Transaction Type"
              placeholder="Select type"
              options={transTypeOptions}
              isRequired={true}
              labelWidth={LW}
              containerClass=""
              parentClass={PARENT_CLASS}
              labelClass={LABEL_CLASS}
            />

            {/* Sub Supply Description — shown only when Sub Type is "Others" (value "8") */}
            {values.transaction_sub_type === "8" && (
              <InputField
                name="sub_supply_desc"
                label="Sub Supply Desc"
                isRequired={true}
                placeholder="Enter sub supply description (max 20 chars)"
                maxLength={20}
                labelWidth={LW}
                containerClass=""
                parentClass={PARENT_CLASS}
                labelClass={LABEL_CLASS}
                allowSpecial={true}
              />
            )}

            {/* Address Details – 4 column display */}
            <div className="flex items-start">
              <label
                className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] flex-shrink-0"
                style={{ minWidth: LW }}
              >
                Address Details
              </label>
              <div className="flex gap-6 flex-1 border border-[var(--input-border)] rounded-[8px] p-4 bg-[var(--input-bg)]">
                <AddressCol
                  title="Dispatch From"
                  lines={storeLines}
                  gstin={storeData?.company_gstn ?? storeDetails?.gstn}
                />
                <div className="w-px bg-[var(--light-border-color)]" />
                <AddressCol
                  title="Bill From"
                  lines={storeLines}
                  gstin={storeData?.company_gstn ?? storeDetails?.gstn}
                />
                <div className="w-px bg-[var(--light-border-color)]" />
                <AddressCol
                  title="Bill To"
                  lines={billToLines}
                  gstin={payAddr?.cmr_pay_gst ?? orderData?.cmr_gstin}
                />
                <div className="w-px bg-[var(--light-border-color)]" />
                <AddressCol
                  title="Ship To"
                  lines={shipToLines}
                  gstin={shipAddr?.cmr_ship_gst ?? payAddr?.cmr_pay_gst ?? orderData?.cmr_gstin}
                />
              </div>
            </div>

            {/* Place of Delivery */}
            <SelectField
              name="place_of_delivery"
              label="Place of Delivery"
              placeholder="Select state"
              options={stateOptions}
              isRequired={true}
              labelWidth={LW}
              containerClass=""
              parentClass={PARENT_CLASS}
              labelClass={LABEL_CLASS}
            />
          </section>

          {/* ── Section 2: Item Details ──────────────────────────────────────── */}
          <section className="py-5 border-b border-[var(--light-border-color)]">
            <button
              type="button"
              onClick={() => setItemDetailsOpen((v) => !v)}
              className="flex items-center gap-2 mb-3 text-[var(--primary)]"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              <span className="font-[600] text-[length:var(--input-label-font-size)]">Item Details</span>
              {itemDetailsOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>

            {itemDetailsOpen && (
              <div>
                <div className="overflow-x-auto border border-[var(--light-border-color)] rounded-[8px]">
                  <table className="w-full" style={{ fontSize: "13px" }}>
                    <thead>
                      <tr className="bg-[var(--create-table-header-bg)] text-white">
                        {["#", "Item & Description", "HSN Code", "Batch #", "Quantity", "Taxable Amount", "SGST", "CGST", "IGST"].map((h) => (
                          <th key={h} className="text-left px-4 py-[10px] font-[500]" style={{ fontSize: "12px" }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {itemData.length > 0 ? (
                        itemData.map((item, itemIdx) => {
                          const batches = Array.isArray(item?.itemBatchData)
                            ? item.itemBatchData
                            : item?.itemBatchData
                              ? [item.itemBatchData]
                              : [{}];

                          const gstRate = parseFloat(item?.item_gst ?? 0);
                          const halfRate = gstRate / 2;

                          return batches.map((batch, batchIdx) => {
                            const taxable = parseFloat(batch?.item_batch_discounted_sales_rate ?? 0);
                            const qty = batch?.item_batch_quantity ?? "—";
                            const batchNo = batch?.item_batch_number ?? "—";

                            /* per-batch tax amounts */
                            const sgstAmt = parseFloat(batch?.item_batch_sgst_amount ?? batch?.item_sgst ?? 0);
                            const cgstAmt = parseFloat(batch?.item_batch_cgst_amount ?? batch?.item_cgst ?? 0);
                            const igstAmt = parseFloat(batch?.item_batch_igst_amount ?? batch?.item_igst ?? item?.item_igst ?? 0);

                            const isFirstBatch = batchIdx === 0;

                            return (
                              <tr
                                key={`${itemIdx}-${batchIdx}`}
                                className="border-t border-[var(--light-border-color)] hover:bg-[var(--row-hover-color)]"
                              >
                                {/* Item-level columns — only on first batch row */}
                                {isFirstBatch && (
                                  <>
                                    <td
                                      className="px-4 py-3 text-[var(--secondary)] align-top"
                                      rowSpan={batches.length}
                                    >
                                      {itemIdx + 1}
                                    </td>
                                    <td
                                      className="px-4 py-3 text-[var(--secondary)] font-[500] align-top"
                                      rowSpan={batches.length}
                                    >
                                      {item?.item_name ?? "—"}
                                    </td>
                                    <td
                                      className="px-4 py-3 text-[var(--secondary)] align-top"
                                      rowSpan={batches.length}
                                    >
                                      {item?.item_hsn ?? "—"}
                                    </td>
                                  </>
                                )}

                                {/* Batch-level columns */}
                                <td className="px-4 py-3 text-[var(--secondary)]">
                                  <span className="text-[var(--placeholder-color)] text-[11px]">Batch</span>
                                  <span className="ml-1 font-[500]">{batchNo}</span>
                                </td>
                                <td className="px-4 py-3 text-[var(--secondary)]">{qty}</td>
                                <td className="px-4 py-3 text-[var(--secondary)]">₹{taxable.toFixed(2)}</td>
                                <td className="px-4 py-3 text-[var(--secondary)]">
                                  <span className="text-[var(--placeholder-color)]">{halfRate}%</span>
                                  {sgstAmt > 0 && <span className="ml-1">₹{sgstAmt.toFixed(2)}</span>}
                                </td>
                                <td className="px-4 py-3 text-[var(--secondary)]">
                                  <span className="text-[var(--placeholder-color)]">{halfRate}%</span>
                                  {cgstAmt > 0 && <span className="ml-1">₹{cgstAmt.toFixed(2)}</span>}
                                </td>
                                <td className="px-4 py-3 text-[var(--secondary)]">₹{igstAmt.toFixed(2)}</td>
                              </tr>
                            );
                          });
                        })
                      ) : (
                        <tr className="border-t border-[var(--light-border-color)]">
                          <td className="px-4 py-3 text-[var(--placeholder-color)] text-center" colSpan={9}>
                            No items available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="flex justify-end mt-3">
                  <div
                    className="border border-[var(--light-border-color)] rounded-[8px] p-4 bg-[var(--input-bg)]"
                    style={{ minWidth: "280px", fontSize: "13px" }}
                  >
                    <div className="flex justify-between mb-2">
                      <span className="text-[var(--nav-heading-color)]">Taxable Amount</span>
                      <span className="text-[var(--secondary)] font-[500]">
                        ₹{parseFloat(orderData?.soit_sub_total ?? 0).toFixed(2)}
                      </span>
                    </div>
                    {parseFloat(orderData?.soit_total_gst ?? 0) > 0 && (
                      <div className="flex justify-between mb-2">
                        <span className="text-[var(--nav-heading-color)]">GST</span>
                        <span className="text-[var(--secondary)] font-[500]">
                          ₹{parseFloat(orderData?.soit_total_gst ?? 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-[var(--light-border-color)] pt-2 mt-2">
                      <span className="font-[700] text-[var(--secondary)]">TOTAL</span>
                      <span className="font-[700] text-[var(--secondary)]">₹{grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── Section 3: Transportation Details + Part B ───────────────────── */}
          <section className="py-5">
            <div className="grid grid-cols-2 gap-10">

              {/* LEFT: Transportation Details */}
              <div className="flex flex-col gap-5">
                <h3
                  className="font-[700] text-[var(--secondary)] uppercase"
                  style={{ fontSize: "12px", letterSpacing: "0.06em" }}
                >
                  Transportation Details
                </h3>

                {/* Transporter Name — optional */}
                <InputField
                  name="transporter_name"
                  label="Transporter Name"
                  placeholder="Enter transporter's name"
                  labelWidth={LW}
                  containerClass=""
                  parentClass={PARENT_CLASS}
                  labelClass={LABEL_CLASS}
                  errMsgContainerClass="hidden"
                  allowSpecial={true}
                />

                {/* Transporter ID (GSTIN) — conditional mandatory (Road + no Vehicle No) */}
                <InputField
                  name="transporter_id"
                  label="Transporter ID"
                  isRequired={values.mode_of_transport === "1" && !values.vehicle_no}
                  placeholder="e.g. 29AAACG0569P1Z3"
                  labelWidth={LW}
                  containerClass=""
                  parentClass={PARENT_CLASS}
                  labelClass={LABEL_CLASS}
                  allowSpecial={true}
                />

                {/* Distance */}
                <div className="flex flex-col gap-1">
                  <InputField
                    name="distance"
                    label="Distance (in Km)"
                    placeholder="Enter distance"
                    type="number"
                    isRequired={true}
                    labelWidth={LW}
                    containerClass=""
                    parentClass={PARENT_CLASS}
                    labelClass={LABEL_CLASS}
                    allowSpecial={false}
                  />
                  <div className="flex items-start justify-between" style={{ paddingLeft: LW }}>
                    <p className="text-[var(--placeholder-color)] flex-1" style={{ fontSize: "11px", lineHeight: "1.5" }}>
                      If you enter 0, e-Way Bill system will automatically calculate it based on the dispatch and delivery locations.
                    </p>
                    <a
                      href="https://maps.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--primary)] font-[500] flex items-center gap-1 whitespace-nowrap hover:underline ml-4 flex-shrink-0"
                      style={{ fontSize: "12px" }}
                    >
                      Calculate Distance
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* RIGHT: Part B */}
              <div className="flex flex-col gap-5">
                <h3
                  className="font-[700] text-[var(--secondary)] uppercase"
                  style={{ fontSize: "12px", letterSpacing: "0.06em" }}
                >
                  Part B
                </h3>

                {/* Mode of Transportation — dynamic from API */}
                <div className="flex items-center">
                  <label
                    className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] flex-shrink-0"
                    style={{ minWidth: LW }}
                  >
                    Mode of Transportation
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {transModeList.map((mode) => {
                      const Icon = MODE_ICONS[mode?.name] ?? Truck;
                      const active = values.mode_of_transport === mode?.value;
                      return (
                        <button
                          key={mode?.value}
                          type="button"
                          onClick={() => setFieldValue("mode_of_transport", mode?.value)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-[8px] border transition-all font-[500] ${active
                            ? "border-[var(--primary)] text-[var(--primary)] bg-[#e6f1ff]"
                            : "border-[var(--input-border)] text-[var(--nav-heading-color)] bg-[var(--input-bg)] hover:border-[var(--primary)]"
                            }`}
                          style={{ fontSize: "13px" }}
                        >
                          <Icon size={14} />
                          {mode?.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Vehicle Type — dynamic from API */}
                <div className="flex items-center">
                  <label
                    className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] flex-shrink-0"
                    style={{ minWidth: LW }}
                  >
                    Vehicle Type
                  </label>
                  <div className="flex items-center gap-6">
                    {vehicleTypeList.map((vt) => (
                      <label
                        key={vt?.value}
                        className="flex items-center gap-2 cursor-pointer text-[var(--input-color)] text-[length:var(--input-font-size)] font-[var(--input-font-weight)]"
                      >
                        <input
                          type="radio"
                          name="vehicle_type"
                          value={vt?.value}
                          checked={values.vehicle_type === vt?.value}
                          onChange={() => setFieldValue("vehicle_type", vt?.value)}
                          className="accent-[var(--primary)] w-4 h-4"
                        />
                        {vt?.name}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Vehicle No — mandatory when Road + no Transporter ID */}
                <InputField
                  name="vehicle_no"
                  label="Vehicle No"
                  isRequired={values.mode_of_transport === "1" && !values.transporter_id}
                  placeholder="e.g. GJ05AA0000"
                  labelWidth={LW}
                  containerClass=""
                  parentClass={PARENT_CLASS}
                  labelClass={LABEL_CLASS}
                  allowSpecial={true}
                />

                {/* Transporter's Doc No — mandatory when Rail/Air/Ship */}
                <InputField
                  name="transporter_doc_no"
                  label="Transporter's Doc No"
                  isRequired={["2", "3", "4"].includes(values.mode_of_transport)}
                  placeholder="Document number"
                  labelWidth={LW}
                  containerClass=""
                  parentClass={PARENT_CLASS}
                  labelClass={LABEL_CLASS}
                  allowSpecial={true}
                />

                {/* Transporter's Doc Date — mandatory when Rail/Air/Ship */}
                <InputField
                  name="transporter_doc_date"
                  label="Transporter's Doc Date"
                  isRequired={["2", "3", "4"].includes(values.mode_of_transport)}
                  placeholder="dd/MM/yyyy"
                  type="date"
                  labelWidth={LW}
                  containerClass=""
                  parentClass={PARENT_CLASS}
                  labelClass={LABEL_CLASS}
                  allowSpecial={true}
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ════ STICKY FOOTER (matches Sales Invoice footer pattern) ═════════ */}
      <div
        className="border-t border-[var(--create-table-footer-border)] bg-white h-[4rem] flex justify-between items-center px-5"
        style={{ boxShadow: "0 -2px 8px rgba(0,0,0,0.04)" }}
      >
        {/* Left — empty spacer (mirrors Sales Invoice layout) */}
        <div />

        {/* Right — action buttons */}
        <div className="flex gap-3 items-center">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
              bg-[var(--primary-button-bg-color)] cursor-pointer
              text-[var(--primary-button-fg-color)]
              border border-[var(--primary-button-bg-color)]
              hover:bg-[var(--primary-button-hover-color)]
              text-[length:var(--primary-button-create-fs)]
              font-[var(--primary-button-font-weight)]
              disabled:opacity-60"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>

          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => { setFieldValue("generate", true); submitForm(); }}
            className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
              bg-[var(--primary-button-bg-color)] cursor-pointer
              text-[var(--primary-button-fg-color)]
              border border-[var(--primary-button-bg-color)]
              hover:bg-[var(--primary-button-hover-color)]
              text-[length:var(--primary-button-create-fs)]
              font-[var(--primary-button-font-weight)]
              disabled:opacity-60"
          >
            Save and Generate
          </button>

          <button
            type="button"
            onClick={() => navigate("/e-way-bills")}
            className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
              bg-[var(--draft-button-bg)] cursor-pointer
              text-[var(--draft-button-fg)]
              border border-[var(--draft-button-border)]
              text-[length:var(--draft-button-fs)]
              font-[var(--draft-button-fw)]"
          >
            Cancel
          </button>
        </div>
      </div>
    </Form>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const EWB_TOKEN_KEY = "ewb_session_token";

const CreateEWayBill = ({ invoiceId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const hasInvoice = invoiceId && invoiceId !== "new";
  const initialPending = hasInvoice ? 3 : 2;

  const [loading, setLoading] = useState(true);
  const [pendingCount, setPending] = useState(initialPending);
  const [invoiceData, setInvoiceData] = useState(null);
  const [storeDetails, setStoreDetails] = useState({});

  const decrement = () =>
    setPending((prev) => {
      const next = prev - 1;
      if (next <= 0) setLoading(false);
      return next;
    });

  useEffect(() => {
    /* ── 1. Auth token ───────────────────────────────────────────── */
    dispatch(
      eWayBillAuthTokenAction(
        (data) => {
          const token =
            data?.authenticationToken ??
            data?.data?.authenticationToken ??
            data?.token ??
            data?.data?.token ??
            null;
          if (token) sessionStorage.setItem(EWB_TOKEN_KEY, token);
          decrement();
        },
        (err) => {
          errorToast(err?.data?.message ?? "Failed to fetch authentication token");
          decrement();
        }
      )
    );

    /* ── 2. Store details ────────────────────────────────────────── */
    dispatch(
      storeDetailsAction(
        (data) => { setStoreDetails(data?.data ?? {}); decrement(); },
        (err) => {
          errorToast(err?.data?.message ?? "Failed to fetch store details");
          decrement();
        }
      )
    );

    /* ── 3. Invoice / dropdown data (only when a real invoice id) ── */
    if (hasInvoice) {
      dispatch(
        eWayBillInvoiceDataAction(
          { soit_id: invoiceId },
          (data) => {
            const raw = data?.data?.[0] ?? data?.data ?? data ?? null;
            setInvoiceData(raw);
            decrement();
          },
          (err) => {
            errorToast(err?.data?.message ?? "Failed to fetch invoice data");
            decrement();
          }
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  if (loading) return <LoadingSpinner />;

  /* ── extract data from API response ── */
  /* ewayBillMastersData is an OBJECT (not array) — access keys directly */
  const masters = invoiceData?.ewayBillMastersData ?? {};
  const dropdownData = {
    docType: masters?.docType ?? [],
    subSupplyType: masters?.subSupplyType ?? [],
    transactionType: masters?.transactionType ?? [],
    states: masters?.states ?? [],
    transMode: masters?.transMode ?? [],
    vehicleType: masters?.vehicleType ?? [],
  };

  const orderRaw = invoiceData?.orderData;
  const orderData = Array.isArray(orderRaw) ? (orderRaw[0] ?? {}) : (orderRaw ?? {});
  const itemData = invoiceData?.itemData ?? [];
  /* storeData returned directly by the invoice API (company_* fields) */
  const storeData = invoiceData?.storeData ?? {};

  /* inv_no from the GET API (not a dropdown) */
  const invNo = invoiceData?.soit_custom_invoice_number ?? orderData?.soit_custom_invoice_number ?? "";

  /* ── totals — sum across ALL batches of ALL items ── */
  const allBatches = itemData.flatMap((item) =>
    Array.isArray(item?.itemBatchData)
      ? item.itemBatchData.map((b) => ({ batch: b, item }))
      : [{ batch: item?.itemBatchData ?? {}, item }]
  );

  const taxableTotal = allBatches.reduce(
    (sum, { batch }) => sum + parseFloat(batch?.item_batch_discounted_sales_rate ?? 0), 0
  );

  /* TOTAL from order — soit_total_amount */
  const grandTotal = parseFloat(orderData?.soit_total_amount ?? 0);

  const sgstTotal = allBatches.reduce(
    (sum, { batch, item }) => sum + parseFloat(batch?.item_batch_sgst_amount ?? batch?.item_sgst ?? item?.item_sgst ?? 0), 0
  );
  const cgstTotal = allBatches.reduce(
    (sum, { batch, item }) => sum + parseFloat(batch?.item_batch_cgst_amount ?? batch?.item_cgst ?? item?.item_cgst ?? 0), 0
  );
  const igstTotal = allBatches.reduce(
    (sum, { batch, item }) => sum + parseFloat(batch?.item_batch_igst_amount ?? batch?.item_igst ?? item?.item_igst ?? 0), 0
  );

  /* ── default to first value of each API list ── */
  const firstVal = (arr) => arr?.[0]?.value ?? "";

  const initialValues = {
    document_type: firstVal(dropdownData.docType),
    transaction_sub_type: firstVal(dropdownData.subSupplyType),
    transaction_type: firstVal(dropdownData.transactionType),
    place_of_delivery: "",
    /* header display */
    doc_no: invNo,
    customer_name_display: orderData?.cmr_name ?? "",
    invoice_date: orderData?.soit_order_date ? moment(orderData.soit_order_date).format("DD/MM/YYYY") : "",
    /* address */
    customer_id: orderData?.cmr_id ?? "",
    ewb_pay_to_address: orderData?.soit_pay_to_address ?? null,
    ewb_ship_to_address: orderData?.soit_ship_to_address ?? null,
    /* transportation */
    transporter_name: "",
    transporter_id: "",
    distance: "",
    /* part B */
    mode_of_transport: firstVal(dropdownData.transMode),
    vehicle_type: firstVal(dropdownData.vehicleType),
    vehicle_no: "",
    transporter_doc_no: "",
    transporter_doc_date: "",
    sub_supply_desc: "",
    generate: false,
  };

  /* ─── EWB mandatory + conditional validations per spec v1.0.0123 ─────────── */
  const ROAD = "1";
  const NON_ROAD = ["2", "3", "4"]; // Rail, Air, Ship
  const SUB_SUPPLY_OTHERS = "8";

  const validationSchema = Yup.object({
    /* Always mandatory */
    document_type: Yup.string().required("Document Type is required"),
    transaction_sub_type: Yup.string().required("Transaction Sub Type is required"),
    transaction_type: Yup.string().required("Transaction Type is required"),
    place_of_delivery: Yup.string().required("Place of Delivery is required"),
    distance: Yup.number()
      .typeError("Distance must be a number")
      .required("Distance is required")
      .min(0, "Distance cannot be negative")
      .max(4000, "Distance must be ≤ 4000 km"),

    /* subSupplyDesc — mandatory when subSupplyType is Others (value "8") */
    sub_supply_desc: Yup.string().when("transaction_sub_type", {
      is: SUB_SUPPLY_OTHERS,
      then: (s) => s.required("Sub Supply Description is required when Sub Type is Others"),
      otherwise: (s) => s.notRequired(),
    }),

    /* ── Part B conditional rules ── */

    /* vehicleType — mandatory when transMode is Road */
    vehicle_type: Yup.string().when("mode_of_transport", {
      is: ROAD,
      then: (s) => s.required("Vehicle Type is required for Road transport"),
      otherwise: (s) => s.notRequired(),
    }),

    /* vehicleNo — mandatory when Road + no Transporter ID
       Uses .test() instead of .when() to avoid Yup cyclic dependency with transporter_id */
    vehicle_no: Yup.string().test(
      "vehicle-no-required",
      "Vehicle No is required when mode is Road and Transporter ID is not entered",
      function (value) {
        const { mode_of_transport, transporter_id } = this.parent;
        if (mode_of_transport === ROAD && !transporter_id) {
          return Boolean(value);
        }
        return true;
      }
    ),

    /* transporterId — mandatory when Road + no Vehicle No
       Uses .test() to avoid Yup cyclic dependency with vehicle_no */
    transporter_id: Yup.string().test(
      "transporter-id-required",
      "Transporter ID is required when mode is Road and Vehicle No is not entered",
      function (value) {
        const { mode_of_transport, vehicle_no } = this.parent;
        if (mode_of_transport === ROAD && !vehicle_no) {
          return Boolean(value);
        }
        return true;
      }
    ),

    /* transDocNo — mandatory when transMode is Rail / Air / Ship */
    transporter_doc_no: Yup.string().when("mode_of_transport", {
      is: (mode) => NON_ROAD.includes(mode),
      then: (s) => s.required("Transporter Doc No is required for Rail / Air / Ship"),
      otherwise: (s) => s.notRequired(),
    }),

    /* transDocDate — mandatory when transMode is Rail / Air / Ship */
    transporter_doc_date: Yup.string().when("mode_of_transport", {
      is: (mode) => NON_ROAD.includes(mode),
      then: (s) => s.required("Transporter Doc Date is required for Rail / Air / Ship"),
      otherwise: (s) => s.notRequired(),
    }),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    const authToken = sessionStorage.getItem(EWB_TOKEN_KEY);

    /* ── helper: look up numeric state code from name using API states list ── */
    const getStateCode = (stateName) => {
      if (!stateName) return null;
      const found = (dropdownData.states ?? []).find(
        (s) => (s.name ?? "").toUpperCase() === (stateName ?? "").toUpperCase()
      );
      const code = found?.code ?? found?.value ?? null;
      return code !== null ? parseInt(code, 10) : null;
    };

    /* ── helper: convert YYYY-MM-DD to DD/MM/YYYY ── */
    const toSlashDate = (dateStr) => {
      if (!dateStr) return "";
      if (dateStr.includes("/")) return dateStr;
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    };

    /* ── doc date (DD/MM/YYYY) and monthYear (MM-YYYY) ── */
    const rawInvoiceDate = orderData?.soit_order_date
      ? moment(orderData.soit_order_date).format("DD/MM/YYYY")
      : moment().format("DD/MM/YYYY");
    const docDate = values.invoice_date || rawInvoiceDate;
    const [dd, mm, yyyy] = docDate.split("/");
    const monthYear = `${mm}-${yyyy}`;

    /* ── resolved addresses ── */
    const payAddr = values.ewb_pay_to_address;
    const shipAddr = values.ewb_ship_to_address;

    /* ── state codes ── */
    const fromStateCode = getStateCode(storeData?.company_state ?? storeDetails?.store_state);
    const toStateName = payAddr?.cmr_pay_address_state ?? orderData?.cmr_billing_state ?? "";
    const toStateCode = getStateCode(toStateName);
    const shipStateName = shipAddr?.cmr_ship_address_state
      ?? orderData?.cmr_shipping_state
      ?? toStateName;
    const shipToStateCode = getStateCode(toStateName);

    /* ── item list — flatten: one entry per batch per item ── */
    const itemList = itemData.flatMap((item) => {
      const batches = Array.isArray(item?.itemBatchData)
        ? item.itemBatchData
        : item?.itemBatchData
          ? [item.itemBatchData]
          : [{}];
      const gstRate = parseFloat(item?.item_gst ?? 0);
      return batches.map((batch) => ({
        item_id: item?.item_id ?? "",
        hsnCode: String(item?.item_hsn ?? ""),
        quantity: parseFloat(batch?.item_batch_quantity ?? 0),
        taxableAmount: parseFloat(batch?.item_batch_discounted_sales_rate ?? 0),
        cgstRate: gstRate / 2,
        sgstRate: gstRate / 2,
        igstRate: parseFloat(batch?.item_batch_igst_amount ?? item?.item_igst ?? 0),
      }));
    });

    const payload = {
      authenticationToken: authToken ?? "",
      monthYear,
      version: "1.0.0123",
      billLists: [
        {
          soit_id: orderData?.soit_id ?? invoiceId ?? "",
          userGstin: storeData?.company_gstn ?? storeDetails?.gstn ?? "",
          supplyType: "O",
          subSupplyType: values.transaction_sub_type,
          subSupplyDesc: values.sub_supply_desc || undefined,
          docType: values.document_type,
          docNo: values.doc_no,
          docDate,
          fromGstin: storeData?.company_gstn ?? storeDetails?.gstn ?? "",
          fromTrdName: storeData?.company ?? storeData?.company_alias_name ?? storeDetails?.store_name ?? "",
          fromAddr1: storeData?.company_address ?? storeDetails?.address_line1 ?? "",
          fromAddr2: storeData?.company_area ?? storeData?.company_landmark ?? storeDetails?.address_line2 ?? "",
          fromPlace: storeData?.company_city ?? storeDetails?.store_city ?? "",
          fromPincode: parseInt(storeData?.company_pincode ?? storeDetails?.pincode ?? 0, 10),
          fromStateCode,
          despatchStateCode: fromStateCode,
          toGstin: shipAddr?.cmr_ship_gst ?? payAddr?.cmr_pay_gst ?? null,
          toTrdName: orderData?.cmr_name ?? "",
          toAddr1: payAddr?.cmr_pay_address_street ?? orderData?.cmr_billing_address1 ?? "",
          toAddr2: payAddr?.cmr_pay_address_address ?? orderData?.cmr_billing_address2 ?? "",
          toPlace: payAddr?.cmr_pay_address_city ?? orderData?.cmr_billing_city ?? "",
          toPincode: parseInt(
            shipAddr?.cmr_ship_address_pincode
            ?? payAddr?.cmr_pay_pincode
            ?? orderData?.cmr_billing_pincode
            ?? 0,
            10
          ),
          toStateCode,
          shiptoStateCode: shipToStateCode,
          transactionType: values.transaction_type,
          totalValue: taxableTotal,
          cgstValue: cgstTotal,
          sgstValue: sgstTotal,
          igstValue: igstTotal,
          cessValue: 0,
          mainHsnCode: String(itemData?.[0]?.item_hsn ?? ""),
          totInvValue: grandTotal,
          transMode: parseInt(values.mode_of_transport ?? 0, 10),
          transDistance: parseInt(values.distance ?? 0, 10),
          transporterName: values.transporter_name || undefined,
          transporterId: values.transporter_id || undefined,
          transDocNo: values.transporter_doc_no || undefined,
          transDocDate: values.transporter_doc_date
            ? toSlashDate(values.transporter_doc_date)
            : undefined,
          vehicleNo: values.vehicle_no || undefined,
          vehicleType: values.vehicle_type || undefined,
          itemList,
        },
      ],
    };

    /* ── helper: extract a readable message from any error shape the API returns ── */
    const extractEWBErrors = (payload) => {
      const messages = [];

      // Shape 1: errors[].errorMessage  (seen in screenshot)
      const errorsArr = payload?.errors ?? payload?.data?.errors ?? [];
      if (Array.isArray(errorsArr) && errorsArr.length > 0) {
        errorsArr.forEach((e) => {
          const msg = e?.errorMessage ?? e?.ErrorInfo ?? e?.message ?? "";
          const col = e?.ColumnName ?? e?.columnName ?? "";
          if (msg) messages.push(col ? `${col}: ${msg}` : msg);
        });
      }

      // Shape 2: data.Result.Response[].ErrorInfo
      const responseArr = payload?.data?.Result?.Response ?? payload?.Result?.Response ?? [];
      if (Array.isArray(responseArr) && responseArr.length > 0) {
        responseArr.forEach((e) => {
          const msg = e?.ErrorInfo ?? e?.errorMessage ?? "";
          const col = e?.ColumnName ?? "";
          if (msg) messages.push(col ? `${col}: ${msg}` : msg);
        });
      }

      // Shape 3: top-level message / Message
      if (messages.length === 0) {
        const fallback =
          payload?.message ??
          payload?.data?.message ??
          payload?.data?.Message ??
          payload?.Message ??
          null;
        if (fallback) messages.push(fallback);
      }

      return messages;
    };

    dispatch(
      eWayBillCreateAction(
        payload,
        (data) => {
          setSubmitting(false);

          /* ── application-level failure (HTTP 200 but status:false or errors present) ── */
          const hasErrors =
            data?.status === false ||
            (Array.isArray(data?.errors) && data.errors.length > 0) ||
            (Array.isArray(data?.data?.errors) && data.data.errors.length > 0);

          if (hasErrors) {
            const msgs = extractEWBErrors(data);
            if (msgs.length > 0) {
              msgs.forEach((m) => errorToast(m));
            } else {
              errorToast("E-Way Bill generation failed");
            }
            return; // stop — do NOT navigate
          }

          /* ── business-level failures inside results[] ── */
          const results = data?.results ?? [];
          const failed = results.filter((r) => !r.success);
          if (failed.length > 0) {
            failed.forEach((r) => {
              const msg = r.message ?? "E-Way Bill generation failed";
              errorToast(msg.replace(/^\d+:/, "").trim());
            });
            return; // stop — do NOT navigate
          }

          /* ── all good ── */
          successToast(data?.message ?? "E-Way Bill saved successfully");
          navigate("/e-way-bills");
        },
        (err) => {
          setSubmitting(false);
          /* HTTP-level errors — surface exact API validation messages */
          const msgs = extractEWBErrors(err?.data ?? err);
          if (msgs.length > 0) {
            msgs.forEach((m) => errorToast(m));
          } else {
            errorToast("Failed to save E-Way Bill");
          }
        }
      )
    );
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <EWayBillFormInner
        orderData={orderData}
        itemData={itemData}
        storeDetails={storeDetails}
        storeData={storeData}
        dropdownData={dropdownData}
        invNo={invNo}
        taxableTotal={taxableTotal}
        igstTotal={igstTotal}
        cgstTotal={cgstTotal}
        sgstTotal={sgstTotal}
        grandTotal={grandTotal}
      />
    </Formik>
  );
};

export default CreateEWayBill;
